<script setup lang="ts">
import { computed, ref } from 'vue';
import { i18n, t, tArray } from '@/i18n';

const compareHeroTitle = computed(() => t('landingPages.aiToolsIndex.seo_content.hero.title'));
const compareHeroSubtitle = computed(() =>
  t('landingPages.aiToolsIndex.seo_content.hero.subtitle')
);
const traditionalTitle = computed(() =>
  t('landingPages.aiToolsIndex.seo_content.compare.traditional.title')
);
const veesoTitle = computed(() => t('landingPages.aiToolsIndex.seo_content.compare.veeso.title'));
const traditionalItems = computed(() =>
  tArray('landingPages.aiToolsIndex.seo_content.compare.traditional.items')
);
const veesoItems = computed(() =>
  tArray('landingPages.aiToolsIndex.seo_content.compare.veeso.items')
);

const stepsTitle = computed(() => t('landingPages.aiToolsIndex.seo_content.steps.title'));
const step1Title = computed(() => t('landingPages.aiToolsIndex.seo_content.steps.1.title'));
const step1Description = computed(() =>
  t('landingPages.aiToolsIndex.seo_content.steps.1.description')
);
const step2Title = computed(() => t('landingPages.aiToolsIndex.seo_content.steps.2.title'));
const step2Description = computed(() =>
  t('landingPages.aiToolsIndex.seo_content.steps.2.description')
);
const step3Title = computed(() => t('landingPages.aiToolsIndex.seo_content.steps.3.title'));
const step3Description = computed(() =>
  t('landingPages.aiToolsIndex.seo_content.steps.3.description')
);

const audienceTitle = computed(() => t('landingPages.aiToolsIndex.seo_content.audience.title'));
const audienceLearnMore = computed(() =>
  t('landingPages.aiToolsIndex.seo_content.audience.learn_more')
);

const audienceCardDefs = [
  {
    slug: 'for-marketers',
    key: 'marketers',
    image: 'https://static.veeso.ai/static-assets/marketer.svg',
  },
  {
    slug: 'for-human-resource',
    key: 'hr',
    image: 'https://static.veeso.ai/static-assets/hr-people.svg',
  },
  {
    slug: 'for-founders',
    key: 'founders',
    image: 'https://static.veeso.ai/static-assets/founder.svg',
  },
  {
    slug: 'for-creators',
    key: 'creators',
    image: 'https://static.veeso.ai/static-assets/content-creator.svg',
  },
  {
    slug: 'for-startups',
    key: 'startups',
    image: 'https://static.veeso.ai/static-assets/startups.svg',
  },
] as const;

const solutions = computed(() =>
  audienceCardDefs.map((card) => ({
    slug: card.slug,
    image: card.image,
    title: t(`landingPages.aiToolsIndex.seo_content.audience.${card.key}.title`),
    description: t(`landingPages.aiToolsIndex.seo_content.audience.${card.key}.description`),
  }))
);

const faqTitle = computed(() => t('landingPages.aiToolsIndex.seo_content.faq.title'));

type FaqItem = {
  question: string;
  answer?: string;
  answerParagraph1?: string;
  answerParagraph2?: string;
  answerParagraph3?: string;
  listItems?: string[];
  products?: string[];
};

const faqItems = computed(() => {
  const raw = i18n.getRaw('landingPages.aiToolsIndex.seo_content.faq.items');
  return Array.isArray(raw) ? (raw as FaqItem[]) : [];
});

const expandedFaqIndex = ref<number | null>(null);

const toggleFaq = (index: number) => {
  expandedFaqIndex.value = expandedFaqIndex.value === index ? null : index;
};
</script>

<template>
  <div class="space-y-24">
    <!-- Why Choose Veeso AI? -->
    <section class="bg-[#f5f5f5] rounded-[32px] p-8 md:p-12">
      <div class="max-w-4xl mx-auto text-center mb-12">
        <h2
          class="text-[28px] leading-[60px] font-normal text-black/90 mb-4 tracking-[-0.8px] font-['Boldonse']"
        >
          {{ compareHeroTitle }}
        </h2>
        <h3 class="text-xl md:text-2xl text-black/60">
          {{ compareHeroSubtitle }}
        </h3>
      </div>

      <div class="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        <div class="bg-white p-8 rounded-2xl border border-black/5">
          <h3 class="text-xl font-bold mb-6 text-black/60">{{ traditionalTitle }}</h3>
          <ul class="space-y-4">
            <li
              v-for="item in traditionalItems"
              :key="item"
              class="flex items-start gap-3 text-black/60"
            >
              <span class="text-red-400">❌</span> {{ item }}
            </li>
          </ul>
        </div>

        <div class="bg-white p-8 rounded-2xl border border-black/5">
          <h3 class="text-xl font-bold mb-6 text-black/90">{{ veesoTitle }}</h3>
          <ul class="space-y-4">
            <li v-for="item in veesoItems" :key="item" class="flex items-start gap-3 text-black/90">
              <span class="text-green-500">✅</span> {{ item }}
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- Simple Three Steps to Generate Designs -->
    <section class="max-w-[1280px] mx-auto mb-24 pt-12">
      <h2
        class="text-[28px] leading-[60px] font-normal text-black/90 mb-12 text-center tracking-[-0.8px] font-['Boldonse']"
      >
        {{ stepsTitle }}
      </h2>
      <div class="grid md:grid-cols-3 gap-[32px] mt-12">
        <div
          class="group relative bg-white rounded-[24px] p-8 border border-black/5 hover:border-black/10 transition-all duration-300"
        >
          <div
            class="absolute -top-[11px] -left-[9px] w-[38.4px] h-[38.4px] rounded-full bg-black flex items-center justify-center text-white text-sm font-bold shadow-[0px_3px_6px_rgba(0,0,0,0.07)]"
          >
            1
          </div>
          <h3
            class="text-[28px] leading-[31px] text-black/90 mb-3 mt-4"
            style="
              font-family:
                'SF Pro',
                -apple-system,
                BlinkMacSystemFont,
                sans-serif;
              font-weight: 274;
              color: rgba(0, 0, 0, 0.9);
            "
          >
            {{ step1Title }}
          </h3>
          <p class="text-base leading-[26px] text-black/60" style="font-size: 16px">
            {{ step1Description }}
          </p>
        </div>

        <div
          class="group relative bg-white rounded-[24px] p-8 border border-black/5 hover:border-black/10 transition-all duration-300"
        >
          <div
            class="absolute -top-[11px] -left-[9px] w-[38.4px] h-[38.4px] rounded-full bg-black flex items-center justify-center text-white text-sm font-bold shadow-[0px_3px_6px_rgba(0,0,0,0.07)]"
          >
            2
          </div>
          <h3
            class="text-[28px] leading-[31px] text-black/90 mb-3 mt-4"
            style="
              font-family:
                'SF Pro',
                -apple-system,
                BlinkMacSystemFont,
                sans-serif;
              font-weight: 274;
              color: rgba(0, 0, 0, 0.9);
            "
          >
            {{ step2Title }}
          </h3>
          <p class="text-base leading-[26px] text-black/60" style="font-size: 16px">
            {{ step2Description }}
          </p>
        </div>

        <div
          class="group relative bg-white rounded-[24px] p-8 border border-black/5 hover:border-black/10 transition-all duration-300"
        >
          <div
            class="absolute -top-[11px] -left-[9px] w-[38.4px] h-[38.4px] rounded-full bg-black flex items-center justify-center text-white text-sm font-bold shadow-[0px_3px_6px_rgba(0,0,0,0.07)]"
          >
            3
          </div>
          <h3
            class="text-[28px] leading-[31px] text-black/90 mb-3 mt-4"
            style="
              font-family:
                'SF Pro',
                -apple-system,
                BlinkMacSystemFont,
                sans-serif;
              font-weight: 274;
              color: rgba(0, 0, 0, 0.9);
            "
          >
            {{ step3Title }}
          </h3>
          <p class="text-base leading-[26px] text-black/60" style="font-size: 16px">
            {{ step3Description }}
          </p>
        </div>
      </div>
    </section>

    <!-- Who Is Veeso AI For? -->
    <section class="bg-[#f5f5f5] rounded-[32px] p-8 md:p-16">
      <h2
        class="text-[28px] leading-[60px] font-normal text-black/90 mb-12 text-center tracking-[-0.8px] font-['Boldonse']"
      >
        {{ audienceTitle }}
      </h2>

      <div class="grid grid-cols-1 md:grid-cols-5 gap-2 max-w-[1320px] mx-auto">
        <NuxtLink
          v-for="solution in solutions"
          :key="solution.slug"
          :to="`/solutions/${solution.slug}`"
          class="group flex flex-col p-2 rounded-xl bg-white overflow-hidden transition-all duration-300 hover:bg-black/5 hover:scale-[1.02]"
        >
          <div
            class="aspect-square w-full rounded-lg overflow-hidden mb-2 bg-black/5 flex items-center justify-center"
          >
            <img :src="solution.image" :alt="solution.title" class="w-full h-full object-cover" />
          </div>
          <h3 class="text-base md:text-lg font-medium text-black/90 px-1">
            {{ solution.title }}
          </h3>
          <p class="text-sm text-black/60 font-normal mb-2 px-1 line-clamp-2">
            {{ solution.description }}
          </p>
          <span
            class="text-sm font-medium text-black/80 mt-auto pt-3 inline-flex items-center gap-1 px-1 pb-1"
          >
            {{ audienceLearnMore }}
            <span class="transition-transform group-hover:translate-x-1">→</span>
          </span>
        </NuxtLink>
      </div>
    </section>

    <!-- FAQ -->
    <section class="max-w-4xl mx-auto">
      <h2
        class="text-[28px] leading-[60px] font-normal text-black/90 mb-12 text-center tracking-[-0.8px] font-['Boldonse']"
      >
        {{ faqTitle }}
      </h2>

      <div class="space-y-4">
        <div
          v-for="(item, index) in faqItems"
          :key="index"
          class="bg-black/[0.03] rounded-3xl px-8 py-6 transition-all duration-300"
        >
          <button
            type="button"
            @click="toggleFaq(index)"
            class="flex items-center justify-between w-full text-left font-medium text-xl text-black/90 focus:outline-none"
          >
            <h3 class="font-medium text-xl text-black/90">{{ item.question }}</h3>
            <span
              class="transform transition-transform duration-300 text-2xl"
              :class="{ 'rotate-45': expandedFaqIndex === index }"
            >
              +
            </span>
          </button>
          <div
            class="grid transition-all duration-300 ease-in-out overflow-hidden"
            :class="
              expandedFaqIndex === index
                ? 'grid-rows-[1fr] opacity-100 mt-4'
                : 'grid-rows-[0fr] opacity-0'
            "
          >
            <div class="overflow-hidden">
              <div v-if="item.answerParagraph1" class="space-y-3">
                <p class="text-black/60 leading-relaxed text-lg">
                  {{ item.answerParagraph1 }}
                </p>
                <ul v-if="item.listItems && item.listItems.length > 0" class="space-y-2 pl-0 mt-2">
                  <li
                    v-for="(listItem, listIndex) in item.listItems"
                    :key="listIndex"
                    class="text-black/60 leading-relaxed text-lg flex items-start"
                  >
                    <span class="text-black/40 mr-3 flex-shrink-0 mt-0.5">•</span>
                    <span>{{ listItem }}</span>
                  </li>
                </ul>
                <p v-if="item.answerParagraph2" class="text-black/60 leading-relaxed text-lg">
                  {{ item.answerParagraph2 }}
                </p>
                <p v-if="item.answerParagraph3" class="text-black/60 leading-relaxed text-lg">
                  {{ item.answerParagraph3 }}
                  <span v-if="item.products && item.products.length > 0">
                    <template v-for="(product, productIndex) in item.products" :key="productIndex">
                      <template v-if="productIndex === 0">{{ product }}</template>
                      <template v-else-if="productIndex === item.products.length - 1"
                        >{{ $t('ui.visibleCopy.componentsAiToolsAiToolsSeoContent.text1') }}
                        {{ product }}</template
                      >
                      <template v-else>, {{ product }}</template>
                    </template>
                    <span>.</span>
                  </span>
                </p>
              </div>
              <p v-else class="text-black/60 leading-relaxed text-lg">
                {{ item.answer }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
